import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { User, Mail, Coins, LogOut, LifeBuoy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import CashOutDialog from '../components/CashOutDialog';
import PotEligibilityCard from '../components/PotEligibilityCard';  // eslint-disable-line no-unused-vars -- community pots removed from UI (Jun 2026)
import KioskPinSection from '../components/KioskPinSection';
import EmailVerifyCard from '../components/EmailVerifyCard';
import EmailManageCard from '../components/EmailManageCard';
import { Button } from '../components/ui/button';
import WinnersWall from '../components/WinnersWall';
import axios from 'axios';
import { useState as useState2, useEffect as useEffect2 } from 'react';
import { FreePlayNote, playable } from '../components/FreePlayNote';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Small chip that tells the player how much Free Play they'll get next
// Monday from the 6% cashout fee. Explicit about "money to play with,
// NOT cash" so nobody expects a wire transfer.
const RebateChip = () => {
    const [data, setData] = useState2(null);
    useEffect2(() => {
        axios.get(`${API_URL}/user/cashout-rebate-preview`)
            .then((r) => setData(r.data))
            .catch(() => setData(null));
    }, []);
    if (!data || (data.pending_rebate || 0) <= 0) return null;
    return (
        <div
            className="rounded-2xl p-4 mb-4"
            style={{
                background: 'linear-gradient(135deg, rgba(255,215,0,0.10), rgba(57,255,20,0.05))',
                border: '1px solid rgba(255,215,0,0.45)',
            }}
            data-testid="profile-rebate-chip"
        >
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#FFD700] font-black">
                        NEXT MONDAY REBATE
                    </div>
                    <div className="text-2xl font-black text-white leading-tight mt-0.5">
                        ${Number(data.pending_rebate).toFixed(2)}
                        <span className="text-[11px] text-[#39FF14] font-bold ml-2">FREE PLAY</span>
                    </div>
                </div>
                <div className="shrink-0 text-4xl">🎁</div>
            </div>
            <div className="text-[11px] text-[#c8b8e8] mt-1.5 leading-snug">
                From {data.cashout_count} recent cashout{data.cashout_count === 1 ? '' : 's'} (6% fee).
                <br />
                <span className="text-[#FFD700] font-black">Free Play = money to bet with, NOT cash.</span>{' '}
                Play through 1× and any winnings become cashable.
            </div>
            <details className="mt-3 group">
                <summary className="text-[11px] text-[#FFD700] font-bold cursor-pointer list-none hover:text-white transition-colors">
                    <span className="inline-block group-open:rotate-90 transition-transform mr-1">▸</span>
                    How the rebate works
                </summary>
                <ol className="text-[11px] text-[#c8b8e8] mt-2 pl-4 space-y-1 leading-snug list-decimal">
                    <li>Cash out anytime — you keep <span className="text-white font-bold">94%</span> immediately (net of the 6% processing fee).</li>
                    <li>Every Monday at 9 AM CT, the <span className="text-[#FFD700] font-bold">6% comes back as Free Play</span>.</li>
                    <li>Play through the Free Play once — any winnings become <span className="text-[#39FF14] font-bold">cashable dollars</span>.</li>
                </ol>
            </details>
        </div>
    );
};

const Profile = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Deep-link support — arriving with #deposit or #wheel-history
    // (e.g. from the Tonk load prompt or the settled-wheel widget)
    // scrolls the matching section into view on mount.
    useEffect(() => {
        const hash = window.location.hash;
        if (!hash) return;
        // #wheel-history → the WinnersWall card at the bottom of the page.
        // Look up its data-testid since it's not a real element id.
        if (hash === '#wheel-history') {
            const el = document.querySelector('[data-testid="profile-wheel-history-section"]');
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
            }
            return;
        }
        if (hash === '#deposit') {
            const el = document.getElementById('deposit');
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
            }
        }
    }, []);

    // Return-to-table param — if the user arrived here from a Tonk seat
    // that they need to fund, offer a one-tap way back to the table.
    const returnTo = (() => {
        try {
            const p = new URLSearchParams(window.location.search).get('return');
            return p && p.startsWith('/') ? p : null;
        } catch { return null; }
    })();

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const isPlayer = user && user.role !== 'admin';

    return (
        <div className="min-h-screen bg-[#050505]">
            <Header />

            <div className="max-w-2xl mx-auto px-4 py-8">
                {returnTo && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-4 rounded-2xl border-2 border-[#F5C542] bg-gradient-to-r from-[#F5C542]/15 to-transparent px-4 py-3 flex items-center justify-between gap-3"
                        data-testid="profile-return-banner"
                    >
                        <div className="text-sm">
                            <div className="text-[#F5C542] font-black uppercase tracking-wider text-xs">
                                Back to your seat
                            </div>
                            <div className="text-white/70 text-[11px]">
                                Load funds below, then jump straight back into the hand.
                            </div>
                        </div>
                        <Button
                            onClick={() => navigate(returnTo)}
                            className="h-9 bg-[#F5C542] hover:bg-[#F5C542]/90 text-black font-black tracking-wider"
                            data-testid="profile-return-btn"
                        >
                            RETURN
                        </Button>
                    </motion.div>
                )}

                {/* Profile Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card-dark p-8 text-center mb-8"
                >
                    <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#FFD700] to-[#FF007F] flex items-center justify-center mb-4">
                        <User className="w-12 h-12 text-black" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-1" data-testid="profile-name">{user?.name}</h1>
                    <p className="text-[#A0AAB2]" data-testid="profile-email">{user?.email}</p>
                    {user?.role === 'admin' && (
                        <span className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40">
                            Admin
                        </span>
                    )}
                </motion.div>

                {/* Balance Card (players only) */}
                {isPlayer && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="card-dark p-6 mb-6"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#A0AAB2] text-sm mb-1">Balance</p>
                                <div className="flex items-center gap-2">
                                    <Coins className="w-6 h-6 text-[#FFD700]" />
                                    <span className="text-3xl font-mono font-bold text-[#FFD700]" data-testid="profile-credits">
                                        {playable(user).toFixed(2)}
                                    </span>
                                </div>
                                <FreePlayNote user={user} testId="profile-free-play" className="mt-1" />
                                <span className="block text-[11px] text-[#A0AAB2]" data-testid="profile-cashable">Cashable ${Number(user?.credits || 0).toFixed(2)}</span>
                            </div>
                            <CashOutDialog />
                        </div>
                        <p className="text-[11px] text-[#A0AAB2] mt-4">
                            Tip: You can also Cash Out directly from the top bar on any page.
                        </p>
                    </motion.div>
                )}

                {/* Community Pot Eligibility (players only) */}
                {/* Community pot eligibility (Community Grand / BEToBuck$) removed from UI (Jun 2026) */}
                {/* {isPlayer && <PotEligibilityCard />} */}

                {/* Kiosk PIN (players only) */}
                {isPlayer && <KioskPinSection />}

                {/* Account Info */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="card-dark p-6 mb-6"
                >
                    <h3 className="text-lg font-bold text-white mb-4">Account Information</h3>
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                            <Mail className="w-5 h-5 text-[#A0AAB2]" />
                            <div>
                                <p className="text-[#A0AAB2] text-xs">Email</p>
                                <p className="text-white">{user?.email}</p>
                                {isPlayer && <EmailManageCard />}
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                            <User className="w-5 h-5 text-[#A0AAB2]" />
                            <div>
                                <p className="text-[#A0AAB2] text-xs">Member Since</p>
                                <p className="text-white">{formatDate(user?.created_at)}</p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Logout */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="mb-3"
                >
                    <Button
                        onClick={() => navigate('/help')}
                        variant="outline"
                        className="w-full py-6 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500/50"
                        data-testid="profile-help-btn"
                    >
                        <LifeBuoy className="w-5 h-5 mr-2" />
                        Help Center · How everything works
                    </Button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.28 }}
                    data-testid="profile-wheel-history-section"
                >
                    <RebateChip />
                    <WinnersWall />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <Button
                        onClick={handleLogout}
                        variant="outline"
                        className="w-full py-6 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
                        data-testid="logout-btn"
                    >
                        <LogOut className="w-5 h-5 mr-2" />
                        Sign Out
                    </Button>
                </motion.div>
            </div>
        </div>
    );
};

export default Profile;
